import React from 'react';
import { IconButton, Stack, Switch, Typography } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import moment from 'moment';
import { CreditSubmission, Proposal } from '@trnsact/trnsact-shared-types';
import { MUITableColumn } from 'components/shared/MUITable';
import { ApprovalTerms } from 'components/CompareApprovals/types';
import {
  ApprovalFormValues,
  CreditSubmissionDocumentRow,
  SubmissionDocumentRaw,
  SubmissionDocumentRow,
} from '../types';
import { DateFormats } from 'constants/common';
export { resolveLatestProposal } from 'modules/creditSubmission/lib';

export const buildParsedDecisionPayload = (approvalValues: ApprovalFormValues, conditions: string[]) => {
  const termLength =
    approvalValues.termLengthMonths.length > 0
      ? Math.max(...approvalValues.termLengthMonths.map(v => parseInt(v, 10)).filter(n => !isNaN(n))) || null
      : null;

  return {
    decisionType: approvalValues.decision || null,
    approvalAmount: approvalValues.approvalAmount,
    termLengthMonths: termLength,
    interestRate: approvalValues.interestRate,
    monthlyPayment: approvalValues.monthlyPayment,
    downPaymentToVendor: approvalValues.downPaymentToVendor,
    minimumRequiredDownPayment: approvalValues.minimumRequiredDownPayment,
    daysToFirstPayment: approvalValues.daysToFirstPayment
      ? parseInt(approvalValues.daysToFirstPayment, 10) || null
      : null,
    applicantName: approvalValues.applicantName || null,
    conditions: conditions.filter(c => c.trim() !== ''),
    stipulations: [] as string[],
  };
};

const resolveFinanceOverrides = (existingFinance: Record<string, any>, approvalValues: ApprovalFormValues) => {
  const terms = approvalValues.termLengthMonths
    .map(t => parseInt(t, 10))
    .filter(n => !isNaN(n))
    .map(term => ({
      term,
      rate:
        approvalValues.interestRate != null
          ? Number(approvalValues.interestRate)
          : (existingFinance.terms?.[0]?.rate ?? 0),
    }));

  const daysToFirstPayment = approvalValues.daysToFirstPayment
    ? parseInt(approvalValues.daysToFirstPayment, 10) || existingFinance.daysToFirstPayment
    : existingFinance.daysToFirstPayment;

  const minimumRequiredDownPayment = approvalValues.minimumRequiredDownPayment
    ? Number(approvalValues.minimumRequiredDownPayment)
    : existingFinance.minimumRequiredDownPayment;

  return { terms, daysToFirstPayment, minimumRequiredDownPayment };
};

export const buildCalculatePaymentsVariables = (snapshot: Record<string, any>, approvalValues: ApprovalFormValues) => {
  const existingFinance = snapshot.paymentOptions?.[0]?.finance ?? {};
  const { terms, daysToFirstPayment, minimumRequiredDownPayment } = resolveFinanceOverrides(
    existingFinance,
    approvalValues
  );
  return {
    input: [
      {
        finance: {
          ...existingFinance,
          maxApprovalAmount: approvalValues.approvalAmount ?? 0,
          terms,
          daysToFirstPayment,
          minimumDownPaymentPercentage: minimumRequiredDownPayment,
        },
      },
    ],
  };
};

export const normalizePaymentsByTerm = (options: any[]): Record<string, any> =>
  options.reduce((acc, payment) => {
    acc[payment.term] = payment;
    return acc;
  }, {});

export const buildProposalCopyDto = (
  snapshot: Record<string, any>,
  approvalValues: ApprovalFormValues,
  pricing: Record<string, any>,
  vendorOpportunityId: string,
  creditSubmissionId: string | undefined
) => {
  const existingFinance = snapshot.paymentOptions?.[0]?.finance ?? {};
  const { terms, daysToFirstPayment, minimumRequiredDownPayment } = resolveFinanceOverrides(
    existingFinance,
    approvalValues
  );

  return {
    vendorOpportunityId,
    creditSubmissionId,
    saleType: snapshot.saleType,
    financeProgramId: snapshot.financeProgramId ?? null,
    sendProposalData: null,
    settings: {
      ...snapshot.settings,
      configuration: {
        ...snapshot.settings?.configuration,
        pricing,
        formData: {
          ...snapshot.settings?.configuration?.formData,
          submission: {
            ...snapshot.settings?.configuration?.formData?.submission,
            maxApprovalAmount: approvalValues.approvalAmount ?? 0,
          },
          financeQuote: {
            ...snapshot.settings?.configuration?.formData?.financeQuote,
            maxApprovalAmount: approvalValues.approvalAmount ?? 0,
            terms: approvalValues.termLengthMonths.filter(t => t !== ''),
            commonRate: approvalValues.interestRate ? Number(approvalValues.interestRate) : null,
            daysToFirstPayment,
            minimumRequiredDownPayment: approvalValues.minimumRequiredDownPayment,
          },
        },
      },
    },
    paymentOptions: {
      finance: {
        ...existingFinance,
        maxApprovalAmount: approvalValues.approvalAmount ?? 0,
        terms,
        daysToFirstPayment,
        minimumDownPaymentPercentage: minimumRequiredDownPayment,
      },
    },
  };
};

export const buildApprovalFormValues = (
  creditSubmission: CreditSubmission & { proposals?: Proposal[]; contingencies: string[] },
  approvalTerms: ApprovalTerms | undefined,
  docFeeTotal: number
): ApprovalFormValues => {
  const termValue = approvalTerms?.termLengthMonths;
  const termArray =
    termValue != null
      ? String(termValue)
          .split(',')
          .map(v => v.trim())
          .filter(Boolean)
      : [];

  return {
    lenderName: creditSubmission.lenderName ?? '',
    decision: creditSubmission.decision ?? '',
    applicantName: (creditSubmission as any).applicantName ?? '',
    approvalAmount: approvalTerms?.approvedAmount ?? creditSubmission.approvedAmount ?? null,
    termLengthMonths: termArray,
    monthlyPayment: approvalTerms?.monthlyPayment != null ? Number(approvalTerms.monthlyPayment) : null,
    interestRate: approvalTerms?.interestRate ?? null,
    downPaymentToVendor: (approvalTerms as any)?.downPaymentToVendor
      ? Number((approvalTerms as any).downPaymentToVendor)
      : null,
    docFees: docFeeTotal || null,
    minimumRequiredDownPayment: approvalTerms?.minimumRequiredDownPayment
      ? Number(approvalTerms.minimumRequiredDownPayment)
      : null,
    daysToFirstPayment: approvalTerms?.daysToFirstPayment != null ? String(approvalTerms.daysToFirstPayment) : '',
  };
};

export const capitalize = (str: string) => (str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '');

export const mapToDecisionLetterRow = (doc: SubmissionDocumentRaw): SubmissionDocumentRow => ({
  id: doc.documentId,
  date: doc.createdDateTime ? moment(doc.createdDateTime).format(DateFormats.Short) : '-',
  uploadedBy: doc.createdBy ?? '-',
  type: capitalize(doc.docType) || '-',
  description: doc.docDescription ?? '-',
  source: doc.source,
});

export const mapToCreditSubDocRow = (
  doc: SubmissionDocumentRaw,
  creditSubmissionId?: string
): CreditSubmissionDocumentRow => {
  let lenderAccess = false;

  if (doc.documentCreditSubmission) {
    const match = doc.documentCreditSubmission.find(s => s.creditSubmissionId === creditSubmissionId);
    lenderAccess = match?.lenderAccess ?? false;
  }

  return {
    id: doc.documentId,
    date: doc.createdDateTime ? moment(doc.createdDateTime).format(DateFormats.Short) : '-',
    uploadedBy: doc.createdBy ?? '-',
    type: capitalize(doc.docType) || '-',
    description: doc.docDescription ?? '-',
    source: doc.source,
    lenderAccess: lenderAccess ? 'true' : 'false',
  };
};

export const handleDocumentView = (documentId: string) => {
  const currentPath = window.location.pathname + window.location.search + '#submissions';
  window.open(`/document/${documentId}/${btoa(currentPath)}`, '_blank', 'noreferrer');
};

export const handleDocumentDownload = (source: string) => {
  window.open(source, '_blank', 'noreferrer');
};

const ActionsCell = ({ source, documentId }: { source: string | null; documentId: string }) =>
  source ? (
    <Stack direction="row" spacing={0.5}>
      <IconButton
        size="small"
        color="primary"
        sx={{ border: '1px solid', borderColor: 'rgba(33, 150, 243, 0.5)', width: 32, height: 32, borderRadius: '4px' }}
        onClick={() => handleDocumentDownload(source)}
      >
        <GetAppIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        color="primary"
        sx={{ border: '1px solid', borderColor: 'rgba(33, 150, 243, 0.5)', width: 32, height: 32, borderRadius: '4px' }}
        onClick={() => handleDocumentView(documentId)}
      >
        <RemoveRedEyeIcon fontSize="small" />
      </IconButton>
    </Stack>
  ) : null;

const headerCell = { sx: { backgroundColor: 'transparent' } };

export const createDecisionLetterColumns = (): MUITableColumn<SubmissionDocumentRow>[] => [
  { header: 'Uploaded On', renderKey: 'date', headerCell },
  { header: 'Uploaded By', renderKey: 'uploadedBy', headerCell },
  { header: 'Type', renderKey: 'type', headerCell },
  { header: 'Description', renderKey: 'description', headerCell },
  {
    header: 'Actions',
    renderKey: 'source',
    headerCell,
    renderCell: (row: SubmissionDocumentRow) => <ActionsCell source={row.source} documentId={row.id} />,
  },
];

export const createSubmissionDocumentsColumns = (
  onToggleLenderAccess: (documentId: string) => void
): MUITableColumn<CreditSubmissionDocumentRow>[] => [
  { header: 'Uploaded On', renderKey: 'date', headerCell },
  { header: 'Uploaded By', renderKey: 'uploadedBy', headerCell },
  { header: 'Type', renderKey: 'type', headerCell },
  { header: 'Description', renderKey: 'description', headerCell },
  {
    header: 'Lender Access',
    renderKey: 'lenderAccess',
    headerCell,
    renderCell: (row: CreditSubmissionDocumentRow) => (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Switch
          size="small"
          color="primary"
          checked={row.lenderAccess === 'true'}
          onChange={() => onToggleLenderAccess(row.id)}
        />
        <Typography variant="body2">Allow</Typography>
      </Stack>
    ),
  },
  {
    header: 'Actions',
    renderKey: 'source',
    headerCell,
    renderCell: (row: CreditSubmissionDocumentRow) => <ActionsCell source={row.source} documentId={row.id} />,
  },
];
